"use client";

import { Check, Copy, Plus, Trash2 } from "lucide-react";
import { useTranslations } from "next-intl";
import toast from "react-hot-toast";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import type { FormField } from "@/lib/database";
import {
	createEmptyTableRow,
	getTableFixedRows,
	isEmptyTableRow,
	parseTsvRows,
	type TableRowValue,
} from "@/lib/fields/table";
import { evaluateConditions } from "@/lib/fields/visibility";

import type { BaseFieldProps } from "../types";
import { applyBuilderMode, getBuilderMode } from "../utils";

/**
 * Поле «Таблица» (t_82191af7).
 * Динамический режим: строки добавляет респондент (авто первая строка +
 * каскад), значение — массив [{ colId: value }, ...].
 * Фиксированный режим (в настройках заданы tableFixedRows): строки заданы
 * редактором, респондент только заполняет ячейки, значение — объект
 * { rowId: { colId: value } }.
 */
export function TableField(props: BaseFieldProps) {
	const { field, value, onChange, disabled } = props;
	const t = useTranslations("forms");
	const builderMode = getBuilderMode(props);

	const columns =
		(field.settings?.tableColumns as FormField[] | undefined) || [];
	const fixedRows = getTableFixedRows(field);
	const isFixed = fixedRows.length > 0;
	const maxRows = Math.max(
		1,
		Math.min(100, field.settings?.tableMaxRows ?? 10),
	);

	const rows: TableRowValue[] = Array.isArray(value) ? value : [];
	// Динамический режим: всегда показываем хотя бы одну пустую строку.
	const displayRows = rows.length > 0 ? rows : [createEmptyTableRow()];

	const setRows = (next: TableRowValue[]) => {
		onChange(next);
	};

	const updateCell = (rowIdx: number, colId: string, cellValue: any) => {
		const base = displayRows.length > 0 ? displayRows : [createEmptyTableRow()];
		const next = base.map((row, i) =>
			i === rowIdx ? { ...row, [colId]: cellValue } : row,
		);
		// Каскад: если последняя строка стала непустой — подкидываем пустую.
		if (
			rowIdx === next.length - 1 &&
			!isEmptyTableRow(next[rowIdx], columns) &&
			next.length < maxRows
		) {
			next.push(createEmptyTableRow());
		}
		setRows(next);
	};

	const updateFixedCell = (
		rowId: string,
		colId: string,
		cellValue: any,
	) => {
		const current =
			value && typeof value === "object" && !Array.isArray(value) ? value : {};
		onChange({
			...current,
			[rowId]: { ...(current[rowId] || {}), [colId]: cellValue },
		});
	};

	const addRow = () => {
		if (displayRows.length >= maxRows) return;
		setRows([...displayRows, createEmptyTableRow()]);
	};

	const duplicateRow = (rowIdx: number) => {
		if (displayRows.length >= maxRows) return;
		const copy = { ...displayRows[rowIdx] };
		const next = [...displayRows];
		next.splice(rowIdx + 1, 0, copy);
		setRows(next);
	};

	const removeRow = (rowIdx: number) => {
		const removed = displayRows[rowIdx];
		const next = displayRows.filter((_, i) => i !== rowIdx);
		setRows(next);
		if (!removed || Object.keys(removed).length === 0) return;
		// Undo восстанавливает строку в СОСТОЯНИЕ ПОСЛЕ удаления (next) на
		// её прежнем индексе — правки, сделанные пока висит тост, не затираются.
		// react-hot-toast не умеет action-кнопку в toast(), поэтому JSX с кнопкой.
		const toastId = toast(
			<span className="flex items-center gap-3">
				<span>{t("table.rowRemoved", { n: rowIdx + 1 })}</span>
				<button
					className="font-medium text-primary underline underline-offset-2"
					onClick={() => {
						toast.dismiss(toastId);
						setRows([
							...next.slice(0, rowIdx),
							removed,
							...next.slice(rowIdx),
						]);
					}}
					type="button"
				>
					{t("table.undo")}
				</button>
			</span>,
			{
				duration: 6000,
				icon: "🗑️",
			},
		);
	};

	const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
		if (disabled || builderMode || isFixed) return;
		const text = e.clipboardData.getData("text/plain");
		if (!text || !text.includes("\t")) return;
		e.preventDefault();
		const { rows: parsed, dropped, skippedEmpty } = parseTsvRows(
			text,
			columns,
			maxRows,
		);
		if (parsed.length === 0) {
			if (skippedEmpty > 0) {
				toast(t("table.pasteEmpty"));
			}
			return;
		}
		setRows(parsed);
		toast.success(
			dropped > 0
				? t("table.pastePartial", { n: parsed.length, count: dropped })
				: t("table.pasteApplied", { n: parsed.length }),
		);
	};

	// Рендер одной ячейки (общий для обоих режимов).
	const renderCell = (
		col: FormField,
		cellValue: any,
		onCellChange: (v: any) => void,
	) => {
		const baseInput = applyBuilderMode(
			{
				className: "h-9 rounded-md border-border/60 text-sm",
				disabled,
				"aria-label": col.label || col.id,
				type: "text",
				value: cellValue ?? "",
			},
			builderMode,
		);

		switch (col.type) {
			case "number":
				return (
					<Input
						{...baseInput}
						inputMode="decimal"
						key={col.id}
						onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
							const raw = e.target.value;
							// Разрешаем набирать "12," / "12.5" / "-3"
							if (!/^-?\d*[.,]?\d*$/.test(raw)) return;
							onCellChange(raw === "" ? "" : Number(raw.replace(",", ".")));
						}}
						placeholder={col.placeholder}
						type="text"
						value={cellValue ?? ""}
					/>
				);
			case "checkbox":
				return (
					<Checkbox
						aria-label={col.label || col.id}
						checked={cellValue === true}
						className="ml-3"
						disabled={disabled}
						key={col.id}
						onCheckedChange={(checked) => onCellChange(checked === true)}
					/>
				);
			case "select":
				return (
					<Select
						key={col.id}
						onValueChange={(v) => onCellChange(v)}
						value={cellValue ?? ""}
						disabled={disabled}
					>
						<SelectTrigger
							aria-label={col.label || col.id}
							className="h-9 border-border/60 text-sm"
						>
							<SelectValue placeholder={col.placeholder || "—"} />
						</SelectTrigger>
						<SelectContent>
							{(col.options || []).map((opt) => {
								const optValue =
									typeof opt === "string" ? opt : (opt?.value ?? "");
								const optLabel =
									typeof opt === "string" ? opt : (opt?.label ?? optValue);
								return (
									<SelectItem key={optValue} value={optValue}>
										{optLabel}
									</SelectItem>
								);
							})}
						</SelectContent>
					</Select>
				);
			default:
				// text / email / phone / link / date — простые инпуты
				return (
					<Input
						{...baseInput}
						key={col.id}
						onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
							onCellChange(e.target.value)
						}
						placeholder={col.placeholder}
						type={col.type === "date" ? "date" : "text"}
					/>
				);
		}
	};

	if (columns.length === 0) {
		return (
			<div className="text-muted-foreground text-sm">
				{t("table.noColumns")}
			</div>
		);
	}

	const renderHeaderCell = (col: FormField) => (
		<th
			className="bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 border-border/60 border-b font-medium text-foreground text-sm"
			key={col.id}
			style={{ minWidth: col.settings?.tableColumnWidth ?? 200 }}
		>
			<div className="flex items-center gap-1 px-3 py-2">
				<span>
					{col.label || "—"}
					{col.required && <span className="ml-0.5 text-destructive">*</span>}
				</span>
			</div>
		</th>
	);

	const visibleColumnsFor = (rowCells: TableRowValue) =>
		columns.filter(
			(col) =>
				!col.settings?.conditionalVisibility?.enabled ||
				evaluateConditions(
					col.settings.conditionalVisibility!,
					rowCells as Record<string, any>,
				),
		);

	const tableShell = (header: React.ReactNode, body: React.ReactNode) => (
		<div className="flex flex-col gap-2" onPaste={handlePaste as any}>
			{builderMode && (
				<div className="rounded-md border border-border/60 bg-muted/40 px-3 py-2 text-muted-foreground text-xs">
					{t("table.builderHint")}
				</div>
			)}
			<div className="overflow-hidden rounded-lg border border-border/60">
				<div className="max-h-96 overflow-auto">
					<table className="w-full border-collapse">
						<thead className="sticky top-0 z-10">{header}</thead>
						<tbody>{body}</tbody>
					</table>
				</div>
			</div>
		</div>
	);

	// ---- Фиксированный режим ----
	if (isFixed) {
		const fixedValue =
			value && typeof value === "object" && !Array.isArray(value) ? value : {};

		const header = (
			<tr>
				<th
					className="bg-background/95 backdrop-blur border-border/60 border-b"
					style={{ width: 160, minWidth: 160 }}
				/>
				{columns.map(renderHeaderCell)}
			</tr>
		);

		const body = fixedRows.map((row) => {
			const cells = fixedValue[row.id] || {};
			const visible = visibleColumnsFor(cells);
			return (
				<tr className="hover:bg-muted/30" key={row.id}>
					<td
						className="border-border/40 border-b px-3 py-1.5 align-middle font-medium text-muted-foreground text-sm"
						style={{ width: 160, minWidth: 160 }}
					>
						{row.label || row.id}
						{row.required && <span className="ml-0.5 text-destructive">*</span>}
					</td>
					{visible.map((col) => (
						<td
							className="border-border/40 border-b px-2 py-1.5 align-middle"
							key={col.id}
							style={{ minWidth: col.settings?.tableColumnWidth ?? 200 }}
						>
							{renderCell(col, cells[col.id], (v) =>
								updateFixedCell(row.id, col.id, v),
							)}
						</td>
					))}
				</tr>
			);
		});

		return tableShell(header, body);
	}

	// ---- Динамический режим ----
	const canAdd = displayRows.length < maxRows && !disabled;

	const renderActionsHeader = () => (
		<th
			aria-label={t("table.rowActions")}
			className="bg-background/95 backdrop-blur border-border/60 border-b"
			key="__actions"
			style={{ width: 88, minWidth: 88 }}
		/>
	);

	const renderRow = (row: TableRowValue, rowIdx: number) => {
		const visibleColumns = visibleColumnsFor(row);
		return (
			<tr className="hover:bg-muted/30" key={rowIdx}>
				{visibleColumns.map((col) => (
					<td
						className="border-border/40 border-b px-2 py-1.5 align-middle"
						key={col.id}
						style={{ minWidth: col.settings?.tableColumnWidth ?? 200 }}
					>
						{renderCell(col, row[col.id], (v) => updateCell(rowIdx, col.id, v))}
					</td>
				))}
				<td
					className="border-border/40 border-b px-1 py-1.5 align-middle"
					style={{ width: 88, minWidth: 88 }}
				>
					<div className="flex items-center gap-0.5">
						<Button
							aria-label={t("table.duplicateRow")}
							className="size-7"
							disabled={disabled || displayRows.length >= maxRows}
							onClick={() => duplicateRow(rowIdx)}
							size="sm"
							tabIndex={-1}
							type="button"
							variant="ghost"
						>
							<Copy aria-hidden="true" className="size-3.5" />
						</Button>
						<Button
							aria-label={t("table.removeRow")}
							className="size-7 text-muted-foreground hover:text-destructive"
							disabled={disabled}
							onClick={() => removeRow(rowIdx)}
							size="sm"
							tabIndex={-1}
							type="button"
							variant="ghost"
						>
							<Trash2 aria-hidden="true" className="size-3.5" />
						</Button>
					</div>
				</td>
			</tr>
		);
	};

	const header = (
		<tr>
			{columns.map(renderHeaderCell)}
			{renderActionsHeader()}
		</tr>
	);

	const body = displayRows.map(renderRow);

	return (
		<>
			{tableShell(header, body)}
			<div className="flex items-center justify-between gap-2">
				<Button
					disabled={!canAdd}
					onClick={addRow}
					size="sm"
					type="button"
					variant="outline"
				>
					<Plus aria-hidden="true" className="mr-1 size-4" />
					{t("table.addRow")}
					{displayRows.length > 0 && (
						<span className="ml-1 text-muted-foreground">
							{displayRows.length}/{maxRows}
						</span>
					)}
				</Button>
				{displayRows.length >= maxRows && (
					<span className="flex items-center gap-1 text-muted-foreground text-xs">
						<Check aria-hidden="true" className="size-3.5" />
						{t("table.maxRowsReached", { n: maxRows })}
					</span>
				)}
			</div>
		</>
	);
}
